import ActionButton from "@/components/Button/ActionButton";
import Icon from "@/components/Icon/Icon";
import { FillScreen, HStack, VStack } from "@/components/Layout";
import { HeaderTabs } from "@/components/Navigation/HeaderTabs";
import { Text } from "@/components/Typography/Text";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { LabelPerson } from "@/features/person/components/LabelPerson";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { Link, Outlet, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useSuspenseQueryOperationPackageInstance } from "./_profile/-queries";
import { usePostOperationPackageInstanceAbort } from "./_profile/-queries/usePostOperationPackageInstanceAbort";
import { TaskStatus } from "./_profile/-types/operation-package";

export const Route = createFileRoute(
  "/_staff/ga-operation-core/operation-packages/$id/patterns/$patternId/instances/$instanceId/_profile",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation(defaultNS, {});

  const { id: packageId, patternId, instanceId } = Route.useParams();
  const { data: instanceData } = useSuspenseQueryOperationPackageInstance({
    packageId,
    patternId,
    instanceId,
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { toast } = useToast();

  // Operation Package InstanceがInProgressかつ、Operation Taskに一つもCompletedが含まれない場合にのみ使用可能
  const { mutate: abortInstance, isPending: isAbortPending } =
    usePostOperationPackageInstanceAbort(packageId, patternId, instanceId, {
      onSuccess: () => {
        toast({
          description: t(
            "features.operationPackageInstance.action.abortSuccess",
          ),
        });
        setDropdownOpen(false);
      },
      onError: (message) => {
        toast({
          variant: "destructive",
          description: message,
        });
      },
    });

  // operationStepsにCompletedステータスのOperationTaskが含まれているかチェック
  const hasCompletedTask = instanceData.operationSteps.some((step) =>
    step.operationTaskInstances.some(
      (task) => task.status === TaskStatus.Completed,
    ),
  );

  const canAbort =
    instanceData.status === TaskStatus.InProgress && !hasCompletedTask;

  return (
    <FillScreen>
      <VStack className="bg-neutral-fill px-12 pt-4">
        <HStack className="items-start justify-between">
          <VStack className="gap-3">
            <Link to="/ga-operation-core">
              <Icon
                iconName="ChevronRight"
                text={t("pages.gaOperationCore.instance.gaOperationCore")}
                variant="small"
                right
              />
            </Link>
            <Text variant="h4">{instanceData.name}</Text>
          </VStack>

          <HStack className="items-center gap-2">
            <VStack className="p-4 gap-2">
              <Text variant="labelSmall" className="text-muted-foreground">
                {t("pages.gaOperationCore.instance.requestedFor")}
              </Text>
              <Link
                to="/person/$id"
                params={{ id: instanceData.target.targetId }}
              >
                <LabelPerson
                  name={instanceData.target.targetName}
                  avatarUrl={instanceData.target.targetAvatarUrl}
                  size="Default"
                  hoverScale={false}
                />
              </Link>
            </VStack>
            <Separator orientation="vertical" />
            {/*
              TODO: 現時点ではすべてのタスクが完了した＝完了なので完了ボタンが不要なのでコメントアウト。今後任意入力のタスクなどが実装された場合は、完了ボタンを復活させる。
              @see https://dress-code-inc.slack.com/archives/C086TDGRAL8/p1746168672912939
            */}
            {/* <CompleteButton
              routeParam={{
                packageId,
                patternId,
                instanceId,
              }}
              useSuspenseQueryOperationPackageInstance={
                useSuspenseQueryOperationPackageInstance
              }
              usePostOperationPackageInstanceApprove={
                usePostOperationPackageInstanceApprove
              }
            /> */}
            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <ActionButton variant="ghost">
                  <Icon iconName="MoreHorizontalIcon" />
                </ActionButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem>
                  <ActionButton
                    variant="ghost"
                    icon="FileXIcon"
                    iconColor="error"
                    onClick={() => abortInstance()}
                    isLoading={isAbortPending}
                    disabled={!canAbort}
                  >
                    {t("features.operationPackageInstance.action.abort")}
                  </ActionButton>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </HStack>
        </HStack>
        <HeaderTabs
          tabs={[
            {
              to: "/ga-operation-core/operation-packages/$id/patterns/$patternId/instances/$instanceId",
              children: t("pages.gaOperationCore.instance.tabs.detail"),
              activeOptions: { exact: true },
            },
            {
              to: "/ga-operation-core/operation-packages/$id/patterns/$patternId/instances/$instanceId/data",
              children: t("pages.gaOperationCore.instance.tabs.data"),
              activeOptions: { exact: true },
            },
            {
              to: "/",
              children: t(
                "pages.gaOperationCore.instance.tabs.operationActivity",
              ),
              disabled: true,
            },
          ]}
        />
      </VStack>
      <Outlet />
    </FillScreen>
  );
}
